import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateOrder, updateOrderStatus } from '../../Store/orders/ordersSlice';
import { getProducts } from '../../Store/products/productsSlice';
import ProductSelectModal from './ProductSelectModal';
import styles from '../../styles/AdminPanel.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import { getBestWholesaleTier, resolveWholesaleTiers } from '../../utils/productPrice';
import { downloadExcel } from '../../utils/exportExcel';

const STATUS_MAP = {
  new: { label: 'Новий', quick: 'Новий' },
  processing: { label: 'Очікує', quick: 'Очікує' },
  shipped: { label: 'Зібрано', quick: 'Зібрано' },
  completed: { label: 'Завершено', quick: 'Завершено' },
  cancelled: { label: 'Скасовано', quick: 'Скасовано' },
};

const QUICK_STATUSES = ['new', 'processing', 'shipped', 'completed'];

const PAYMENT_OPTIONS = [
  'Оплата карткою',
  'Накладений платіж',
  'Онлайн оплата',
  'Готівковий розрахунок',
];

const CARRIER_OPTIONS = [
  'Нова пошта',
  'Укрпочта',
  'Самовивіз',
  "Кур'єр",
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getItemCost = (item, products) => {
  const product = products.find((p) => p.id === item.id);
  if (!product) return Math.round((Number(item.price) || 0) * 0.85);
  const tier = getBestWholesaleTier(product);
  if (tier?.price) return Number(tier.price);
  const legacy = Number(product.wholesalePrice);
  if (legacy > 0) return legacy;
  const tiers = resolveWholesaleTiers(product);
  if (tiers[0]?.price) return Number(tiers[0].price);
  return Math.round((Number(item.price) || 0) * 0.85);
};

const OrderForm = ({ order, onCancel, onSuccess, onDelete, showMessage }) => {
  const dispatch = useDispatch();
  const { list: products } = useSelector((state) => state.products);
  const { isLoading } = useSelector((state) => state.orders);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    telephone: '',
    citi: '',
    department: '',
    carrier: '',
    payment: '',
    status: 'new',
    trackingNumber: '',
    isPaid: false,
    deliveryComment: '',
    adminNote: '',
    items: [],
  });

  useEffect(() => {
    dispatch(getProducts());
  }, [dispatch]);

  useEffect(() => {
    if (order) {
      setFormData({
        firstName: order.firstName || '',
        lastName: order.lastName || '',
        telephone: order.telephone || '',
        citi: order.citi || '',
        department: order.department || '',
        carrier: order.carrier || '',
        payment: order.payment || '',
        status: order.status || 'new',
        trackingNumber: order.trackingNumber || '',
        isPaid: Boolean(order.isPaid),
        deliveryComment: order.deliveryComment || '',
        adminNote: order.adminNote || '',
        items: order.items || [],
      });
    }
  }, [order]);

  const totals = useMemo(() => {
    const itemsTotal = formData.items.reduce(
      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 1),
      0,
    );
    const costTotal = formData.items.reduce(
      (sum, item) => sum + getItemCost(item, products) * (Number(item.quantity) || 1),
      0,
    );
    const profit = Math.max(0, itemsTotal - costTotal);
    return { itemsTotal, costTotal, profit };
  }, [formData.items, products]);

  const history = useMemo(() => {
    const events = [
      { label: 'Створено замовлення', date: order?.createdAt },
    ];
    if (order?.status && order.status !== 'new') {
      events.push({
        label: `Статус: ${STATUS_MAP[order.status]?.label || order.status}`,
        date: order?.updatedAt || order?.createdAt,
      });
    }
    if (order?.trackingNumber) {
      events.push({ label: `ТТН: ${order.trackingNumber}`, date: order?.updatedAt || order?.createdAt });
    }
    if (order?.isPaid) {
      events.push({ label: 'Оплачено', date: order?.updatedAt || order?.createdAt });
    }
    return events;
  }, [order]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleItemQty = (index, delta) => {
    setFormData((prev) => {
      const items = [...prev.items];
      const qty = Math.max(1, (Number(items[index].quantity) || 1) + delta);
      items[index] = { ...items[index], quantity: qty };
      return { ...prev, items };
    });
  };

  const handleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        [field]: field === 'quantity' || field === 'price' ? Math.max(0, parseInt(value, 10) || 0) : value,
      };
      return { ...prev, items };
    });
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleProductSelect = (selectedProduct) => {
    const full = products.find((p) => p.id === selectedProduct.id);
    const entry = {
      id: selectedProduct.id,
      title: selectedProduct.title,
      article: full?.article || selectedProduct.article || '',
      price: selectedProduct.price,
      quantity: 1,
      images: selectedProduct.images || [],
    };
    const existingIndex = formData.items.findIndex((item) => item.id === entry.id);
    if (existingIndex !== -1) {
      setFormData((prev) => {
        const items = [...prev.items];
        items[existingIndex] = {
          ...items[existingIndex],
          quantity: (Number(items[existingIndex].quantity) || 1) + 1,
        };
        return { ...prev, items };
      });
      showMessage('success', 'Кількість товару збільшено');
    } else {
      setFormData((prev) => ({ ...prev, items: [...prev.items, entry] }));
      showMessage('success', 'Товар додано до замовлення');
    }
  };

  const buildOrderPayload = () => ({
    firstName: formData.firstName,
    lastName: formData.lastName,
    telephone: formData.telephone,
    citi: formData.citi,
    department: formData.department,
    carrier: formData.carrier,
    payment: formData.payment,
    status: formData.status,
    trackingNumber: formData.trackingNumber,
    isPaid: formData.isPaid,
    deliveryComment: formData.deliveryComment,
    adminNote: formData.adminNote,
    userId: order?.userId || null,
  });

  const validate = () => {
    if (!formData.firstName || !formData.lastName || !formData.telephone) {
      showMessage('error', 'Заповніть дані покупця');
      return false;
    }
    if (!formData.citi || !formData.department || !formData.carrier) {
      showMessage('error', 'Заповніть дані доставки');
      return false;
    }
    if (!formData.payment) {
      showMessage('error', 'Оберіть спосіб оплати');
      return false;
    }
    if (formData.items.length === 0) {
      showMessage('error', 'Додайте хоча б один товар');
      return false;
    }
    return true;
  };

  const saveOrder = async (partial = {}) => {
    if (!validate()) return false;
    setSaving(true);
    try {
      await dispatch(updateOrder({
        id: order.id,
        orderData: { ...buildOrderPayload(), ...partial },
        cart: formData.items,
      })).unwrap();
      showMessage('success', 'Замовлення збережено');
      onSuccess();
      return true;
    } catch (error) {
      showMessage('error', error || 'Помилка при збереженні замовлення');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveOrder();
  };

  const handleQuickStatus = async (status) => {
    if (status === formData.status) return;
    setSaving(true);
    try {
      const updated = await dispatch(updateOrderStatus({
        id: order.id,
        status,
        trackingNumber: formData.trackingNumber || undefined,
        isPaid: formData.isPaid,
        deliveryComment: formData.deliveryComment,
        adminNote: formData.adminNote,
      })).unwrap();
      setFormData((prev) => ({ ...prev, status: updated.status || status }));
      showMessage('success', `Статус: ${STATUS_MAP[status]?.label || status}`);
    } catch (error) {
      showMessage('error', error || 'Не вдалося оновити статус');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptOrder = () => handleQuickStatus('processing');

  const handleSaveTtn = async () => {
    setSaving(true);
    try {
      await dispatch(updateOrderStatus({
        id: order.id,
        status: formData.status,
        trackingNumber: formData.trackingNumber.trim(),
        isPaid: formData.isPaid,
        deliveryComment: formData.deliveryComment,
        adminNote: formData.adminNote,
      })).unwrap();
      showMessage('success', 'ТТН збережено');
    } catch (error) {
      showMessage('error', error || 'Не вдалося зберегти ТТН');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async () => {
    setSaving(true);
    try {
      await dispatch(updateOrderStatus({
        id: order.id,
        status: formData.status,
        trackingNumber: formData.trackingNumber || undefined,
        isPaid: formData.isPaid,
        deliveryComment: formData.deliveryComment,
        adminNote: formData.adminNote,
      })).unwrap();
      showMessage('success', 'Примітку збережено');
    } catch (error) {
      showMessage('error', error || 'Не вдалося зберегти примітку');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const text = [
      `Замовлення №${order.id}`,
      `${formData.firstName} ${formData.lastName}`,
      formData.telephone,
      `Сума: ${totals.itemsTotal} ₴`,
      `Статус: ${STATUS_MAP[formData.status]?.label}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      showMessage('success', 'Дані замовлення скопійовано');
    } catch {
      showMessage('error', 'Не вдалося скопіювати');
    }
  };

  const handlePrint = () => {
    const rows = formData.items.map((item) => `
      <tr>
        <td>${item.title}</td>
        <td>${item.article || '—'}</td>
        <td>${item.quantity}</td>
        <td>${item.price} ₴</td>
        <td>${(item.price || 0) * (item.quantity || 1)} ₴</td>
      </tr>
    `).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Замовлення №${order.id}</title>
      <style>body{font-family:sans-serif;padding:24px}table{width:100%;border-collapse:collapse}td,th{border:1px solid #ccc;padding:8px;text-align:left}</style>
      </head><body>
      <h1>Замовлення №${order.id}</h1>
      <p>${formData.firstName} ${formData.lastName}, ${formData.telephone}</p>
      <p>${formData.citi}, отд. ${formData.department}</p>
      <table><thead><tr><th>Товар</th><th>Артикул</th><th>К-сть</th><th>Ціна</th><th>Сума</th></tr></thead><tbody>${rows}</tbody></table>
      <p><strong>Разом: ${totals.itemsTotal} ₴</strong></p>
      </body></html>`;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.print();
    }
  };

  const handleExportExcel = () => {
    const headers = ['Товар', 'Артикул', 'К-сть', 'Ціна', 'Сума'];
    const rows = formData.items.map((item) => [
      item.title || '',
      item.article || '',
      item.quantity,
      item.price,
      (item.price || 0) * (item.quantity || 1),
    ]);
    rows.push(['', '', '', 'Разом', totals.itemsTotal]);
    downloadExcel(`order-${order.id}`, 'Замовлення', headers, rows);
  };

  const statusLabel = STATUS_MAP[formData.status]?.label || formData.status;
  const busy = isLoading || saving;

  return (
    <div className={styles.orderFormPage}>
      <form className={styles.orderFormCard} onSubmit={handleSubmit}>
        <div className={styles.orderFormHeader}>
          <div className={styles.orderFormHeaderLeft}>
            <div className={styles.orderFormHeaderIcons}>
              <button type="button" className={styles.orderFormIconBtn} title="Копіювати" onClick={handleCopy}>📋</button>
              {onDelete && (
                <button
                  type="button"
                  className={`${styles.orderFormIconBtn} ${styles.orderFormIconBtnDanger}`}
                  title="Видалити"
                  onClick={() => onDelete(order)}
                >
                  🗑
                </button>
              )}
            </div>
            <div>
              <h2>Замовлення №{order?.id}</h2>
              <span>{formatDateTime(order?.createdAt)}</span>
            </div>
          </div>
          <div className={styles.orderFormHeaderRight}>
            <span className={`${styles.orderFormStatusBadge} ${styles[`orderFormStatus_${formData.status}`]}`}>
              {statusLabel}
            </span>
            {formData.status === 'new' && (
              <button type="button" className={styles.orderFormAcceptBtn} onClick={handleAcceptOrder} disabled={busy}>
                Прийняти замовлення
              </button>
            )}
            <button type="submit" className={styles.orderFormSaveBtn} disabled={busy}>
              {busy ? 'Збереження…' : 'Зберегти'}
            </button>
          </div>
        </div>

        <div className={styles.orderFormBody}>
          <div className={styles.orderFormMain}>
            <section className={styles.orderFormSection}>
              <div className={styles.orderFormSectionHead}>
                <h3>Товари в замовленні</h3>
              </div>
              <div className={styles.orderFormItems}>
                {formData.items.map((item, index) => {
                  const lineTotal = (Number(item.price) || 0) * (Number(item.quantity) || 1);
                  return (
                    <div key={`${item.id}-${index}`} className={styles.orderFormItem}>
                      <div className={styles.orderFormItemPreview}>
                        <img src={assetUrl(item.images?.[0] || '')} alt={item.title} />
                      </div>
                      <div className={styles.orderFormItemInfo}>
                        <div className={styles.orderFormItemTitle}>{item.title}</div>
                        <div className={styles.orderFormItemSku}>
                          {item.article ? `Артикул: ${item.article}` : `ID: ${item.id || '—'}`}
                        </div>
                      </div>
                      <div className={styles.orderFormItemQty}>
                        <button type="button" onClick={() => handleItemQty(index, -1)}>−</button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        />
                        <button type="button" onClick={() => handleItemQty(index, 1)}>+</button>
                      </div>
                      <div className={styles.orderFormItemPrice}>
                        <input
                          type="number"
                          min="0"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                        />
                        <span>₴</span>
                      </div>
                      <div className={styles.orderFormItemTotal}>{lineTotal} ₴</div>
                      <button type="button" className={styles.orderFormItemRemove} onClick={() => removeItem(index)} title="Видалити">
                        ×
                      </button>
                    </div>
                  );
                })}
                <button type="button" className={styles.orderFormAddItem} onClick={() => setIsProductModalOpen(true)}>
                  + Додати товар
                </button>
              </div>
            </section>

            <section className={styles.orderFormSection}>
              <div className={styles.orderFormSectionHead}>
                <h3>Покупець</h3>
              </div>
              <div className={styles.orderFormGrid2}>
                <div className={styles.orderFormField}>
                  <label htmlFor="firstName">Ім'я</label>
                  <input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required />
                </div>
                <div className={styles.orderFormField}>
                  <label htmlFor="lastName">Прізвище</label>
                  <input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required />
                </div>
                <div className={styles.orderFormField}>
                  <label htmlFor="telephone">Телефон</label>
                  <input id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} required />
                </div>
                <div className={styles.orderFormField}>
                  <label>Email</label>
                  <input value={order?.User?.email || ''} readOnly className={styles.orderFormReadonly} />
                </div>
              </div>
              {order?.User && (
                <div className={styles.orderFormCustomerMeta}>Зареєстрований клієнт · {order.User.name}</div>
              )}
            </section>

            <section className={styles.orderFormSection}>
              <div className={styles.orderFormSectionHead}>
                <h3>Оплата</h3>
              </div>
              <div className={styles.orderFormField}>
                <select id="payment" name="payment" value={formData.payment} onChange={handleChange} required>
                  <option value="">Оберіть спосіб оплати</option>
                  {PAYMENT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                  {!PAYMENT_OPTIONS.includes(formData.payment) && formData.payment && (
                    <option value={formData.payment}>{formData.payment}</option>
                  )}
                </select>
              </div>
              <label className={styles.orderFormCheckbox}>
                <input type="checkbox" name="isPaid" checked={formData.isPaid} onChange={handleChange} />
                Оплачено
              </label>
            </section>

            <section className={styles.orderFormSection}>
              <div className={styles.orderFormSectionHead}>
                <h3>Доставка</h3>
              </div>
              <div className={styles.orderFormGrid2}>
                <div className={styles.orderFormField}>
                  <label htmlFor="carrier">Перевізник</label>
                  <select id="carrier" name="carrier" value={formData.carrier} onChange={handleChange} required>
                    <option value="">Оберіть перевізника</option>
                    {CARRIER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    {!CARRIER_OPTIONS.includes(formData.carrier) && formData.carrier && (
                      <option value={formData.carrier}>{formData.carrier}</option>
                    )}
                  </select>
                </div>
                <div className={styles.orderFormField}>
                  <label htmlFor="citi">Місто</label>
                  <input id="citi" name="citi" value={formData.citi} onChange={handleChange} required />
                </div>
                <div className={styles.orderFormField}>
                  <label htmlFor="department">Відділення / адреса</label>
                  <input id="department" name="department" value={formData.department} onChange={handleChange} required />
                </div>
                <div className={styles.orderFormField}>
                  <label htmlFor="trackingNumber">Номер ТТН</label>
                  <input
                    id="trackingNumber"
                    name="trackingNumber"
                    value={formData.trackingNumber}
                    onChange={handleChange}
                    placeholder="Введіть номер накладної"
                  />
                </div>
              </div>
              <div className={styles.orderFormField}>
                <label htmlFor="deliveryComment">Коментар до доставки</label>
                <textarea
                  id="deliveryComment"
                  name="deliveryComment"
                  value={formData.deliveryComment}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Коментар для кур'єра або відділення"
                />
              </div>
              <button type="button" className={styles.orderFormTtnBtn} onClick={handleSaveTtn} disabled={busy}>
                Зберегти ТТН
              </button>
            </section>

            <section className={styles.orderFormSection}>
              <div className={styles.orderFormSectionHead}>
                <h3>Історія замовлення</h3>
              </div>
              <ul className={styles.orderFormHistory}>
                {history.map((event, idx) => (
                  <li key={idx}>
                    <span className={styles.orderFormHistoryDot} />
                    <div>
                      <strong>{event.label}</strong>
                      <span>{formatDateTime(event.date)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className={styles.orderFormSidebar}>
            <div className={styles.orderFormSidebarBlock}>
              <h4>Разом</h4>
              <div className={styles.orderFormTotalRow}>
                <span>Товари</span>
                <strong>{totals.itemsTotal} ₴</strong>
              </div>
              <div className={styles.orderFormTotalRow}>
                <span>Доставка</span>
                <span className={styles.orderFormTotalMuted}>За тарифами перевізника</span>
              </div>
              <div className={`${styles.orderFormTotalRow} ${styles.orderFormTotalMain}`}>
                <span>До оплати</span>
                <strong>{totals.itemsTotal} ₴</strong>
              </div>
              <div className={styles.orderFormProfit}>
                Прибуток: {totals.profit.toFixed(2)} ₴
              </div>
            </div>

            <div className={styles.orderFormSidebarBlock}>
              <h4>Дії</h4>
              <div className={styles.orderFormQuickStatus}>
                {QUICK_STATUSES.map((st) => (
                  <button
                    key={st}
                    type="button"
                    className={formData.status === st ? styles.orderFormQuickStatusActive : ''}
                    onClick={() => handleQuickStatus(st)}
                    disabled={busy}
                  >
                    {STATUS_MAP[st]?.quick}
                  </button>
                ))}
              </div>
              <div className={styles.orderFormDocBtns}>
                <button type="button" onClick={handlePrint}>Роздрукувати чек</button>
                <button type="button" onClick={handlePrint}>Роздрукувати накладну</button>
                <button type="button" onClick={handleExportExcel}>Експорт в Excel</button>
              </div>
            </div>

            <div className={styles.orderFormSidebarBlock}>
              <h4>Примітка</h4>
              <textarea
                name="adminNote"
                value={formData.adminNote}
                onChange={handleChange}
                rows={4}
                placeholder="Внутрішня нотатка для співробітників"
                className={styles.orderFormNoteArea}
              />
              <div className={styles.orderFormNoteActions}>
                <button type="button" onClick={onCancel}>Отменить</button>
                <button type="button" className={styles.orderFormNoteSave} onClick={handleSaveNote} disabled={busy}>
                  Зберегти
                </button>
              </div>
            </div>
          </aside>
        </div>
      </form>

      <ProductSelectModal
        products={products}
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSelect={handleProductSelect}
        title="Додати товар до замовлення"
      />
    </div>
  );
};

export default OrderForm;
